// ─────────────────────────────────────────────────────────────
// NexiMap — 넥시 창고 1층 배치도 (v7.346 — 원본 도면에서 실측하여 재작성)
//
// ⚠ v7.339~345 의 좌표는 스크린샷을 보고 손으로 짠 근사치였다. 원본과 여러 군데 어긋났다.
//   이번 판은 다르다: 구글시트 [찾기] 탭에 박혀 있던 도면 이미지(xl/media/image1.jpg,
//   1341×1600)를 꺼내 픽셀 단위로 측정한 값이다. 눈대중이 아니다.
//
// 좌표계: viewBox "0 0 1345 1505" — 원본 이미지 픽셀 그대로다.
//   부모 폭에 맞춰 비율 유지로 늘어난다. 원본과 대조할 때 숫자를 그대로 비교하면 된다.
//
// 실측 결과 (원본 이미지 픽셀) — 손으로 짰을 때 틀렸던 것들:
//   파렛트 열 경계 x = 14 · 146 · 272 · 403 · 538
//   블 행 경계   y = 236 · 383 · 530 · 677 · 823    (4행, 각 ≈147)
//   레 행 경계   y = 830 · 989 · 1148 · 1307 · 1465 (4행, 각 ≈159)
//   ⚠ 블과 레는 행 높이가 다르다. 같은 격자로 그리면 안 된다
//   중앙 랙 4칸  x = 750 · 836 · 922 · 1009 (폭 84)
//   중앙 랙 5칸  x = 697 · 777 · 856 · 936 · 1016 (폭 78) — 4칸보다 왼쪽에서 시작
//   우측 랙      x = 1154 · 1240 (폭 84)
//   세로 진열대  4칸 그룹 옆 x 728~747 / 5칸 그룹 옆 x 676~695 (폭 20)
//   ⚠ H6~H10 행만 높이가 66 이다 (다른 랙 행은 34)
//   ⚠ I1 은 36 인데 I2 는 119 다. I2~I5 도 서로 높이가 다르다
//   ⚠ 상단 벽은 단이 져 있다 — 좌우는 y210~223, 가운데는 y225~232
//
// 색도 원본에서 뽑았다:
//   블 #DDF5FF / 레 #F3C0C9 / 그린 #CCFEF2 / 옐1 #FEFCCC / 랙·I열 #F0F0F0
// ─────────────────────────────────────────────────────────────
import React, { useMemo } from 'react';

const VB_W = 1345, VB_H = 1505;

// ─── 파렛트 격자 ─────────────────────────────────────────────
const PAL_X = [14, 146, 272, 403, 538];
const BLUE_Y = [236, 383, 530, 677, 823];
const RED_Y = [830, 989, 1148, 1307, 1465];
// 화면 왼쪽 열이 큰 번호다 (13-16 / 9-12 / 5-8 / 1-4)
const PAL_COLS = [[13, 14, 15, 16], [9, 10, 11, 12], [5, 6, 7, 8], [1, 2, 3, 4]];

// ─── I 열 ────────────────────────────────────────────────────
const I_X0 = 545, I_X1 = 630;
const I_CELLS = [
  { code: 'I1', y0: 840, y1: 876 },
  { code: 'I2', y0: 877, y1: 996 },
  { code: 'I3', y0: 997, y1: 1177 },
  { code: 'I4', y0: 1178, y1: 1298 },
  { code: 'I5', y0: 1299, y1: 1396 },
];

// ─── 중앙 랙 ─────────────────────────────────────────────────
const RK4_X = [750, 836, 922, 1009], RK4_W = 84;
const RK5_X = [697, 777, 856, 936, 1016], RK5_W = 78;
const SHELF4_X = 728, SHELF5_X = 676, SHELF_W = 20;

// shelf: 그룹 왼쪽에 세워둔 진열대 (그 자체가 위치다 — 그룹 라벨이 아니다)
const RACK_GROUPS = [
  { shelf: 'A0', sy0: 511, sy1: 544, cols: RK4_X, w: RK4_W, sx: SHELF4_X,
    rows: [{ y0: 511, y1: 544, codes: ['A1', 'A2', 'A3', 'A4'] }] },
  { shelf: 'B0', sy0: 587, sy1: 657, cols: RK4_X, w: RK4_W, sx: SHELF4_X,
    rows: [{ y0: 588, y1: 621, codes: ['A5', 'A6', 'A7', 'A8'] },
           { y0: 623, y1: 657, codes: ['B1', 'B2', 'B3', 'B4'] }] },
  { shelf: 'C0', sy0: 700, sy1: 769, cols: RK4_X, w: RK4_W, sx: SHELF4_X,
    rows: [{ y0: 700, y1: 733, codes: ['B5', 'B6', 'B7', 'B8'] },
           { y0: 736, y1: 769, codes: ['C1', 'C2', 'C3', 'C4'] }] },
  { shelf: 'D0', sy0: 813, sy1: 882, cols: RK4_X, w: RK4_W, sx: SHELF4_X,
    rows: [{ y0: 813, y1: 846, codes: ['C5', 'C6', 'C7', 'C8'] },
           { y0: 849, y1: 882, codes: ['D1', 'D2', 'D3', 'D4'] }] },
  { shelf: 'E0', sy0: 925, sy1: 994, cols: RK4_X, w: RK4_W, sx: SHELF4_X,
    rows: [{ y0: 925, y1: 958, codes: ['D5', 'D6', 'D7', 'D8'] },
           { y0: 961, y1: 994, codes: ['E1', 'E2', 'E3', 'E4'] }] },
  { shelf: 'F0', sy0: 1037, sy1: 1106, cols: RK5_X, w: RK5_W, sx: SHELF5_X,
    rows: [{ y0: 1037, y1: 1070, codes: ['E5', 'E6', 'E7', 'E8', 'E9'] },
           { y0: 1073, y1: 1106, codes: ['F1', 'F2', 'F3', 'F4', 'F5'] }] },
  { shelf: 'G0', sy0: 1151, sy1: 1220, cols: RK5_X, w: RK5_W, sx: SHELF5_X,
    rows: [{ y0: 1151, y1: 1184, codes: ['F6', 'F7', 'F8', 'F9', 'F10'] },
           { y0: 1186, y1: 1220, codes: ['G1', 'G2', 'G3', 'G4', 'G5'] }] },
  { shelf: null, cols: RK5_X, w: RK5_W,
    rows: [{ y0: 1264, y1: 1297, codes: ['H1', 'H2', 'H3', 'H4', 'H5'] }] },
  // ⚠ H6~H10 만 높이가 66 이다 (다른 랙 행은 34)
  { shelf: null, cols: RK5_X, w: RK5_W,
    rows: [{ y0: 1339, y1: 1404, codes: ['H6', 'H7', 'H8', 'H9', 'H10'] }] },
];

// ─── 우측 랙 ─────────────────────────────────────────────────
const RT_X = [1154, 1240], RT_W = 84;
const RIGHT_GROUPS = [
  { rows: [{ y0: 511, y1: 544, codes: ['J1', 'J2'] }] },
  { rows: [{ y0: 582, y1: 615, codes: ['J3', 'J4'] }, { y0: 618, y1: 651, codes: ['K1', 'K2'] }] },
  { rows: [{ y0: 696, y1: 729, codes: ['K3', 'K4'] }] },
  { rows: [{ y0: 761, y1: 794, codes: ['L1', 'L2'] }, { y0: 797, y1: 830, codes: ['M1', 'M2'] }] },
  { rows: [{ y0: 870, y1: 903, codes: ['M3', 'M4'] }, { y0: 906, y1: 939, codes: ['N1', 'N2'] }] },
  { rows: [{ y0: 991, y1: 1024, codes: ['N3', 'N4'] }, { y0: 1027, y1: 1060, codes: ['O1', 'O2'] }] },
  { rows: [{ y0: 1094, y1: 1128, codes: ['O3', 'O4'] }, { y0: 1130, y1: 1163, codes: ['P1', 'P2'] }] },
  { rows: [{ y0: 1194, y1: 1227, codes: ['P3', 'P4'] }, { y0: 1230, y1: 1263, codes: ['Q1', 'Q2'] }] },
  { rows: [{ y0: 1286, y1: 1319, codes: ['Q3', 'Q4'] }] },
];
const YEL = { x0: 1157, y0: 1333, x1: 1317, y1: 1400 };

// ─── 그린룸 / 사앞 ───────────────────────────────────────────
const GREEN = { x0: 532, y0: 12, x1: 918, y1: 303 };
const GREEN1 = { x0: 636, y0: 14, x1: 916, y1: 208 };       // 초록 파선 영역
const STAIRS = [
  { x0: 715, y0: 12, x1: 825, y1: 105 },                    // 위쪽 계단
  { x0: 655, y0: 100, x1: 755, y1: 198 },                   // 아래쪽 계단
];
const SAAP = { x0: 930, y0: 330, x1: 1012, y1: 368 };

// ─── 벽 ── 원본에서 실측한 굵은 검정 막대 [x0,y0,x1,y1] ──────
const WALLS = [
  [532, 0, 1340, 14],       // 홀 상단 외벽
  [1325, 0, 1340, 1498],    // 우측 외벽
  [532, 0, 546, 223],       // 그린룸 좌벽
  [0, 210, 132, 223],       // 좌상단 벽 — 단이 진 왼쪽
  [414, 210, 546, 223],     // 좌상단 벽 — 단이 진 오른쪽
  [131, 225, 415, 232],     // ⚠ 가운데만 한 단 아래다
  [0, 210, 13, 1498],       // 좌측 외벽
  [540, 511, 544, 1498],    // 파렛트동 ↔ 홀 내벽
  [542, 505, 628, 514],     // I 열 위 짧은 벽
  [686, 312, 1003, 316],    // 그린룸 아래 벽
  [1132, 312, 1340, 316],
  [540, 1408, 1099, 1413],  // 하단 벽 — 문 앞에서 끊긴다
  [1148, 1408, 1340, 1413],
  [1174, 1420, 1181, 1498], // 하단 문 옆 짧은 세로벽
  [0, 1484, 1340, 1498],    // 하단 외벽
];

// ─── 문 ── 원본의 호(arc) 표기를 옮겼다 ──────────────────────
const DOORS = [
  { leaf: 'M532 20H480', arc: 'M480 20A52 52 0 0 1 532 72' },      // 그린룸 좌측 쌍여닫이 (위)
  { leaf: 'M532 155H480', arc: 'M480 155A52 52 0 0 0 532 103' },   // 그린룸 좌측 쌍여닫이 (아래)
  { leaf: 'M910 228H855', arc: 'M855 228A55 55 0 0 0 910 283' },   // 그린룸 우하단
  { leaf: 'M1175 1462H1110', arc: 'M1110 1462A54 54 0 0 1 1164 1408' }, // 홀 하단
];

// ─── 전체 코드 목록 ──────────────────────────────────────────
export const NEXI_CODES = (() => {
  const out = [];
  PAL_COLS.forEach(col => col.forEach(n => out.push('블' + n)));
  PAL_COLS.forEach(col => col.forEach(n => out.push('레' + n)));
  I_CELLS.forEach(c => out.push(c.code));
  RACK_GROUPS.forEach(g => {
    if (g.shelf) out.push(g.shelf);
    g.rows.forEach(r => r.codes.forEach(c => out.push(c)));
  });
  RIGHT_GROUPS.forEach(g => g.rows.forEach(r => r.codes.forEach(c => out.push(c))));
  out.push('옐1', '그린1', '그린2', '사앞');
  return out;
})();

// 입력 정규화 — 공백 제거 + 영문 대문자화. 한글(블/레/그린/옐/사앞)은 그대로.
export const normalizeNexiCode = (raw) => String(raw || '').replace(/\s+/g, '').toUpperCase();
const CODE_SET = new Set(NEXI_CODES.map(normalizeNexiCode));
export const isNexiCode = (raw) => CODE_SET.has(normalizeNexiCode(raw));

// ─── 스타일 ──────────────────────────────────────────────────
const CSS = `
.nx-cell rect { transition: fill .15s, stroke .15s; }
.nx-cell text { pointer-events: none; user-select: none; }
.nx-cell[data-click="1"] { cursor: pointer; }
.nx-cell[data-click="1"]:hover rect.nx-body { stroke: #2563eb; stroke-width: 4; }
.nx-hit rect.nx-body { fill: #2563eb !important; stroke: #1d4ed8 !important; stroke-width: 4 !important; }
.nx-hit text { fill: #ffffff !important; font-weight: 800 !important; }
.nx-ring { fill: none; stroke: #2563eb; stroke-width: 6; }
@keyframes nxPulse {
  0%   { opacity: .95; stroke-width: 6; }
  70%  { opacity: 0;   stroke-width: 22; }
  100% { opacity: 0;   stroke-width: 22; }
}
.nx-ring-anim { animation-name: nxPulse; animation-duration: 1.5s; animation-timing-function: ease-out; animation-iteration-count: infinite; }
@media (prefers-reduced-motion: reduce) {
  /* 애니메이션만 끈다 — 하이라이트 표시는 남는다 (v7.320 교훈) */
  .nx-ring-anim { animation-name: none; opacity: .9; }
}
`;

const THEME = {
  light: {
    wall: '#000000', door: '#111111',
    blue: '#ddf5ff', blueLine: '#c3cdf2', blueText: '#1e3a8a',
    red: '#f3c0c9', redLine: '#d9c1c4', redText: '#c02040',
    rack: '#f0f0f0', rackLine: '#000000', rackText: '#111111',
    green: '#ccfef2', greenLine: '#0b6b4f', greenText: '#0b6b4f',
    yellow: '#fefccc', yellowLine: '#8a7a12', yellowText: '#8a7a12',
  },
  dark: {
    wall: '#e2e8f0', door: '#94a3b8',
    blue: 'rgba(96,165,250,0.18)', blueLine: 'rgba(147,180,232,0.5)', blueText: '#bfdbfe',
    red: 'rgba(248,113,113,0.18)', redLine: 'rgba(234,176,182,0.5)', redText: '#fecaca',
    rack: '#1b2434', rackLine: '#93a3b8', rackText: '#e2e8f0',
    green: 'rgba(52,211,153,0.18)', greenLine: '#34d399', greenText: '#a7f3d0',
    yellow: 'rgba(250,204,21,0.2)', yellowLine: '#ca8a04', yellowText: '#fde68a',
  },
};

// ─── 칸 ──────────────────────────────────────────────────────
function Cell({ code, x, y, w, h, fill, stroke, sw, fs, fw, color, hit, onSelect, vertical, ls }) {
  const cx = x + w / 2, cy = y + h / 2;
  // vertical: 세로 진열대(A0~G0) — 글자를 한 자씩 세로로 쌓는다.
  //   ⚠ writing-mode 는 렌더러 편차가 커서 tspan dy 로 직접 쌓는다.
  const chars = vertical ? String(code).split('') : null;
  const gap = fs * 1.02;
  return (
    <g className={'nx-cell' + (hit ? ' nx-hit' : '')} data-click={onSelect ? '1' : '0'}
      onClick={onSelect ? () => onSelect(code) : undefined}>
      <rect className="nx-body" x={x} y={y} width={w} height={h}
        fill={fill} stroke={stroke} strokeWidth={sw} />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
        fontSize={fs} fontWeight={fw} fill={color} letterSpacing={ls}>
        {chars
          ? chars.map((ch, i) => (
              <tspan key={i} x={cx} dy={i === 0 ? -((chars.length - 1) * gap) / 2 : gap}>{ch}</tspan>
            ))
          : code}
      </text>
      {hit && <rect className="nx-ring nx-ring-anim" x={x} y={y} width={w} height={h} />}
    </g>
  );
}

/**
 * @param {string}   highlight  하이라이트할 위치 코드
 * @param {function} onSelect   칸 클릭 콜백
 * @param {boolean}  dark
 */
export default function NexiMap({ highlight, onSelect, dark = false }) {
  const T = dark ? THEME.dark : THEME.light;
  const hit = useMemo(() => normalizeNexiCode(highlight), [highlight]);
  const is = (code) => !!hit && normalizeNexiCode(code) === hit;

  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%"
      style={{ display: 'block', fontFamily: 'inherit' }}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* ── 그린룸 ── */}
      <g className={'nx-cell' + (is('그린2') ? ' nx-hit' : '')} data-click={onSelect ? '1' : '0'}
        onClick={onSelect ? () => onSelect('그린2') : undefined}>
        <rect className="nx-body" x={GREEN.x0} y={GREEN.y0}
          width={GREEN.x1 - GREEN.x0} height={GREEN.y1 - GREEN.y0}
          fill={T.green} stroke={T.greenLine} strokeWidth={2} />
        <text x={GREEN.x0 + 108} y={GREEN.y1 - 55} textAnchor="middle" dominantBaseline="central"
          fontSize={40} fontWeight={800} fill={T.greenText}>그린2</text>
        {is('그린2') && <rect className="nx-ring nx-ring-anim" x={GREEN.x0} y={GREEN.y0}
          width={GREEN.x1 - GREEN.x0} height={GREEN.y1 - GREEN.y0} />}
      </g>

      {/* 그린1 — 초록 파선 영역 + 계단 2개 */}
      <g className={'nx-cell' + (is('그린1') ? ' nx-hit' : '')} data-click={onSelect ? '1' : '0'}
        onClick={onSelect ? () => onSelect('그린1') : undefined}>
        <rect className="nx-body" x={GREEN1.x0} y={GREEN1.y0}
          width={GREEN1.x1 - GREEN1.x0} height={GREEN1.y1 - GREEN1.y0}
          fill="transparent" stroke={T.greenLine} strokeWidth={5} strokeDasharray="18 12" />
        {STAIRS.map((s, i) => (
          <g key={i}>
            <rect x={s.x0} y={s.y0} width={s.x1 - s.x0} height={s.y1 - s.y0}
              fill="none" stroke={T.door} strokeWidth={4} />
            {Array.from({ length: 7 }, (_, k) => s.x0 + ((k + 1) * (s.x1 - s.x0)) / 8).map((x, k) => (
              <line key={k} x1={x} y1={s.y0} x2={x} y2={s.y1} stroke={T.door} strokeWidth={3.5} />
            ))}
          </g>
        ))}
        <text x={785} y={112} textAnchor="middle" dominantBaseline="central"
          fontSize={34} fontWeight={800} fill={T.greenText}>그린1</text>
        {is('그린1') && <rect className="nx-ring nx-ring-anim" x={GREEN1.x0} y={GREEN1.y0}
          width={GREEN1.x1 - GREEN1.x0} height={GREEN1.y1 - GREEN1.y0} />}
      </g>

      {/* ── 사앞 ── */}
      <Cell code="사앞" x={SAAP.x0} y={SAAP.y0} w={SAAP.x1 - SAAP.x0} h={SAAP.y1 - SAAP.y0}
        fill={T.rack} stroke={T.rackLine} sw={2} fs={26} fw={700} color={T.rackText}
        hit={is('사앞')} onSelect={onSelect} />

      {/* ── 파렛트: 블(파랑) ── */}
      {PAL_COLS.map((col, ci) => col.map((n, ri) => (
        <Cell key={'b' + n} code={'블' + n}
          x={PAL_X[ci]} y={BLUE_Y[ri]} w={PAL_X[ci + 1] - PAL_X[ci]} h={BLUE_Y[ri + 1] - BLUE_Y[ri]}
          fill={T.blue} stroke={T.blueLine} sw={3} fs={30} fw={700} color={T.blueText}
          hit={is('블' + n)} onSelect={onSelect} />
      )))}

      {/* ── 파렛트: 레(빨강) ── ⚠ 블보다 행 높이가 크다 */}
      {PAL_COLS.map((col, ci) => col.map((n, ri) => (
        <Cell key={'r' + n} code={'레' + n}
          x={PAL_X[ci]} y={RED_Y[ri]} w={PAL_X[ci + 1] - PAL_X[ci]} h={RED_Y[ri + 1] - RED_Y[ri]}
          fill={T.red} stroke={T.redLine} sw={3} fs={30} fw={700} color={T.redText}
          hit={is('레' + n)} onSelect={onSelect} />
      )))}
      {/* 레 블록 오른쪽 아래 작은 칸 — 원본에도 라벨이 없다 */}
      <rect x={455} y={1412} width={80} height={60} fill="none" stroke={T.redLine} strokeWidth={3} />

      {/* ── I 열 ── */}
      {I_CELLS.map(c => (
        <Cell key={c.code} code={c.code} x={I_X0} y={c.y0} w={I_X1 - I_X0} h={c.y1 - c.y0}
          fill={T.rack} stroke={T.rackLine} sw={2.5} fs={28} fw={700} color={T.rackText}
          ls="3" hit={is(c.code)} onSelect={onSelect} />
      ))}

      {/* ── 중앙 랙 ── */}
      {RACK_GROUPS.map((g, gi) => (
        <g key={'rg' + gi}>
          {g.rows.map((row) => row.codes.map((code, ci) => (
            <Cell key={code} code={code}
              x={g.cols[ci]} y={row.y0} w={g.w} h={row.y1 - row.y0}
              fill={T.rack} stroke={T.rackLine} sw={2} fs={26} fw={700} color={T.rackText}
              hit={is(code)} onSelect={onSelect} />
          )))}
          {/* A0~G0 은 그룹 라벨이 아니라 세로로 세워둔 진열대 = 그 자체가 위치다 */}
          {g.shelf && (
            <Cell code={g.shelf} x={g.sx} y={g.sy0} w={SHELF_W} h={g.sy1 - g.sy0}
              fill={T.rack} stroke={T.rackLine} sw={2} fs={19} fw={700} color={T.rackText}
              hit={is(g.shelf)} onSelect={onSelect} vertical />
          )}
        </g>
      ))}

      {/* ── 우측 랙 ── */}
      {RIGHT_GROUPS.map((g, gi) => (
        <g key={'rt' + gi}>
          {g.rows.map((row) => row.codes.map((code, ci) => (
            <Cell key={code} code={code}
              x={RT_X[ci]} y={row.y0} w={RT_W} h={row.y1 - row.y0}
              fill={T.rack} stroke={T.rackLine} sw={2} fs={26} fw={700} color={T.rackText}
              hit={is(code)} onSelect={onSelect} />
          )))}
        </g>
      ))}

      {/* ── 옐1 ── */}
      <Cell code="옐1" x={YEL.x0} y={YEL.y0} w={YEL.x1 - YEL.x0} h={YEL.y1 - YEL.y0}
        fill={T.yellow} stroke={T.yellowLine} sw={3} fs={32} fw={800} color={T.yellowText}
        hit={is('옐1')} onSelect={onSelect} />

      {/* ── 벽 ── 원본 실측 */}
      {WALLS.map(([x0, y0, x1, y1], i) => (
        <rect key={'w' + i} x={x0} y={y0} width={x1 - x0} height={y1 - y0}
          fill={T.wall} shapeRendering="crispEdges" />
      ))}

      {/* ── 문 ── */}
      {DOORS.map((d, i) => (
        <g key={'d' + i}>
          <path d={d.arc} fill="none" stroke={T.door} strokeWidth={3} />
          <path d={d.leaf} fill="none" stroke={T.door} strokeWidth={4} />
        </g>
      ))}
    </svg>
  );
}
